import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useLocalStorage } from "../../auth/useLocalStorage";
import { useShippingCalculator } from "../../hooks/useShippingCalculator";
import { usePosConfig } from "../../hooks/usePosConfig";
import { Box, Typography, Paper, Card, CardContent, IconButton } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PlaceIcon from "@mui/icons-material/Place";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearIcon from "@mui/icons-material/Clear";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const containerStyle = {
  width: "100%",
  height: "calc(100vh - 180px)", // Altura dinámica para llenar la ventana sin scroll
};

interface IMarker {
  lat: number;
  lng: number;
}

export const ShippingQuote: React.FC = () => {
  const [user] = useLocalStorage("user", null);

  const defaultCenter = {
    lat: Number(user?.user?.warehouse?.lat ?? 0),
    lng: Number(user?.user?.warehouse?.lng ?? 0),
  };

  const { calculateShipping, isLoaded } = useShippingCalculator();
  const { config, loading: configLoading } = usePosConfig();

  const [center, setCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(16);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [shippingPrice, setShippingPrice] = useState(0);
  const [distance, setDistance] = useState(0);
  const [time, setTime] = useState("");
  const [markers, setMarkers] = useState<IMarker[]>([defaultCenter]);
  const [address, setAddress] = useState("");
  // Último punto cotizado; si config/pos aún no había cargado, se recalcula
  // solo en cuanto la config real esté lista (ver efecto abajo).
  const [lastPoint, setLastPoint] = useState<IMarker | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Icono personalizado para el marcador de la sucursal
  const getBranchIcon = () => {
    if (typeof google !== 'undefined' && google.maps) {
      return {
        url: `${process.env.PUBLIC_URL}/img/logo1.png`,
        scaledSize: new google.maps.Size(50, 50),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(25, 50),
      };
    }
    return undefined;
  };

  const getDistance = (point: IMarker) => {
    setLastPoint(point);
    if (configLoading) return;
    // Cálculo de precio sin orderTotal, ya que es solo cotización (sin envío gratis).
    calculateShipping(defaultCenter, point, config).then((result) => {
      // No pisar el input con `result.destinationAddress`: es un geocodificado
      // inverso de Distance Matrix que puede "redondear" al número de puerta
      // más cercano catalogado (ej. 700 en vez de 704).
      setShippingPrice(result.price);
      setDistance(result.distanceMeters);
      setTime(result.durationText);
    });
  };
  // Si se cotizó antes de que cargara la config real, recalcula automático.
  useEffect(() => {
    if (!configLoading && lastPoint) {
      getDistance(lastPoint);
    }
  }, [configLoading]);
  // El listener del autocomplete (abajo) se registra una sola vez; este ref
  // evita que quede con una versión obsoleta de getDistance (mismo problema
  // que en Map.tsx: si isLoaded llega antes que la config real, el closure
  // original vería configLoading=true para siempre).
  const getDistanceRef = useRef(getDistance);
  useEffect(() => {
    getDistanceRef.current = getDistance;
  });

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    setMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback() {
    setMap(null);
  }, []);

  const geocode = (request: google.maps.GeocoderRequest): void => {
    const geocoder = new google.maps.Geocoder();
    geocoder
      .geocode(request)
      .then((result) => {
        const { results } = result;
        const resultPoint = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        setMarkers([resultPoint]);
        setCenter(resultPoint);
        setZoom(16);
        getDistance(resultPoint);
        // Al arrastrar/clickear el mapa no hay texto tecleado/seleccionado,
        // así que aquí sí usamos el geocodificado inverso propio (única
        // fuente de dirección disponible para este flujo).
        if (inputRef.current) {
          inputRef.current.value = results[0].formatted_address;
        }
      })
      .catch((e) => {
        console.log("Geocode was not successful for the following reason: " + e);
      });
  };

  const onDragMarker = (coord: any) => {
    const { latLng } = coord;
    const lat = latLng.lat();
    const lng = latLng.lng();
    setMarkers([{ lat, lng }]);
    geocode({
      location: { lat, lng },
    });
  };

  const onMapClick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMarkers([{ lat, lng }]);
      geocode({
        location: { lat, lng },
      });
    }
  };

  const handleReset = () => {
    // Limpiar el input
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    // Resetear a la posición inicial
    setMarkers([defaultCenter]);
    setCenter(defaultCenter);
    setZoom(16);
    // Resetear valores
    setShippingPrice(0);
    setDistance(0);
    setTime("");
    setAddress("");
  };

  useEffect(() => {
    if (!isLoaded || !map) return;

    const input = inputRef.current;
    if (!input) return;

    const autocomplete = new google.maps.places.Autocomplete(input);
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place.geometry?.location) {
        const newPoint = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setMarkers([newPoint]);
        setCenter(newPoint);
        setAddress(inputRef.current?.value as string);
        getDistanceRef.current(newPoint);
      }
    });
  }, [isLoaded, map]);

  return (
    <Box sx={{ position: "relative", height: "100%", overflow: "hidden" }}>
      {/* Contenedor del mapa con paneles flotantes */}
      <Box sx={{ position: "relative", borderRadius: "8px", overflow: "hidden", height: "100%" }}>
        {isLoaded ? (
          <>
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={zoom}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={onMapClick}
              options={{
                styles: [
                  {
                    featureType: "poi",
                    elementType: "labels",
                    stylers: [{ visibility: "off" }],
                  },
                ],
                fullscreenControl: false,
                mapTypeControl: false,
                streetViewControl: false,
              }}
            >
              {/* Marcador de la sucursal (fijo) */}
              <Marker
                position={defaultCenter}
                icon={getBranchIcon()}
                title="La Biferia - Sucursal"
                animation={google.maps.Animation.DROP}
              />

              {/* Marcador de destino (arrastrable) */}
              {markers.map((marker, i) => (
                <Marker
                  position={marker}
                  key={i}
                  draggable={true}
                  onDragEnd={onDragMarker}
                  animation={google.maps.Animation.DROP}
                  title="Ubicación de entrega"
                />
              ))}
            </GoogleMap>

            {/* Barra de búsqueda flotante en la parte superior del mapa */}
            <Box
              sx={{
                position: "absolute",
                top: 20,
                left: "50%",
                transform: "translateX(-50%)",
                width: "90%",
                maxWidth: 600,
                zIndex: 10,
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Box sx={{ position: "relative", flex: 1 }}>
                <input
                  ref={inputRef}
                  id="pac-input"
                  type="text"
                  placeholder="🔍 Busque la dirección de entrega o arrastre el marcador en el mapa"
                  className="input-custom"
                  autoComplete="off"
                  style={{
                    width: "100%",
                    padding: "14px 20px",
                    fontSize: "16px",
                    borderRadius: "8px",
                    border: "2px solid #555",
                    backgroundColor: "rgba(42, 42, 42, 0.95)",
                    backdropFilter: "blur(10px)",
                    color: "#fff",
                    outline: "none",
                    transition: "border-color 0.3s, box-shadow 0.3s",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#1976d2";
                    e.target.style.boxShadow = "0 4px 20px rgba(25, 118, 210, 0.4)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#555";
                    e.target.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.3)";
                  }}
                />
              </Box>

              {/* Botón de Limpiar */}
              <IconButton
                onClick={handleReset}
                sx={{
                  backgroundColor: "rgba(42, 42, 42, 0.95)",
                  backdropFilter: "blur(10px)",
                  border: "2px solid #555",
                  color: "#fff",
                  width: 50,
                  height: 50,
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                  transition: "all 0.3s",
                  "&:hover": {
                    backgroundColor: "rgba(244, 67, 54, 0.9)",
                    borderColor: "#f44336",
                    boxShadow: "0 4px 20px rgba(244, 67, 54, 0.4)",
                    transform: "scale(1.1)",
                  },
                }}
                title="Limpiar búsqueda"
              >
                <ClearIcon />
              </IconButton>
            </Box>

            {/* Panel flotante de información - lado izquierdo centrado verticalmente */}
            <Card
              sx={{
                position: "absolute",
                top: "50%",
                left: 20,
                transform: "translateY(-50%)",
                width: 320,
                backgroundColor: "rgba(33, 33, 33, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                zIndex: 10,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#fff",
                    fontWeight: 600,
                  }}
                >
                  <LocalShippingIcon color="primary" />
                  Información de Envío
                </Typography>

                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {/* Distancia */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PlaceIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: "#ccc" }}>
                      Distancia:
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#fff", fontWeight: 500, ml: "auto" }}>
                      {(distance / 1000).toFixed(2)} km
                    </Typography>
                  </Box>

                  {/* Tiempo */}
                  {time && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccessTimeIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: "#ccc" }}>
                        Tiempo estimado:
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#fff", fontWeight: 500, ml: "auto" }}>
                        {time}
                      </Typography>
                    </Box>
                  )}

                  {/* Costo de envío */}
                  <Box
                    sx={{
                      mt: 1,
                      pt: 2,
                      borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#ccc", mb: 0.5 }}>
                      Costo de Envío
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        color: "#4caf50",
                        fontWeight: 700,
                        textShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                      }}
                    >
                      ${shippingPrice.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="600px"
            sx={{ backgroundColor: "#1a1a1a", borderRadius: "8px" }}
          >
            <Typography sx={{ color: "#ccc" }}>Cargando mapa...</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

